import { useState } from 'react'
import {
  AlertTriangle,
  Brain,
  ChevronDown,
  ChevronRight,
  Radar,
  Users,
  Wrench,
} from 'lucide-react'
import { cn } from '../lib/utils'
import type {
  LifecycleInvestigation,
  LifecycleTier,
  LifecycleToolCall,
} from '../types'

/**
 * Read-only rendering of a ws9.lifecycle.v1 investigation record: each
 * L1/L2/L3 tier with its decision, reasoning, and every tool call with what
 * it returned. No data is invented — anything absent is shown as absent
 * (§7 honest alerting). Optimised for operator density, not calm.
 */

const TIER_LABELS: Record<string, string> = {
  l1: 'L1 Triage',
  l2: 'L2 Investigation',
  l3: 'L3 Hunt',
}

function parseToolResult(raw: string | null): unknown | null {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    // Backend truncates long results; a truncated string is not valid JSON.
    return null
  }
}

// ── Tenant activity window (canary correlation) ─────────────────────

interface TenantActivityAccount {
  account_id?: string
  upn?: string | null
  display_name?: string | null
  events?: Array<{
    at?: string
    event_type?: string
    location?: { city?: string; country?: string } | null
    device?: string | null
    session_id?: string | null
  }>
}

function TenantActivityResult({ call }: { call: LifecycleToolCall }) {
  const parsed = parseToolResult(call.result) as {
    window_start?: string
    window_end?: string
    accounts_active?: TenantActivityAccount[]
    accounts_with_no_activity?: Array<{ account_id?: string; upn?: string }>
    total_events?: number
  } | null

  if (!parsed) return <RawResult call={call} />

  const active = parsed.accounts_active ?? []
  const quiet = parsed.accounts_with_no_activity ?? []

  return (
    <div className="mt-1.5 rounded border border-brass/30 bg-brass/5 p-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-brass-dark mb-1.5">
        Who was active when the trap sprang
      </p>
      <p className="text-[11px] text-gray-600 mb-2">
        Window {parsed.window_start ?? '?'} → {parsed.window_end ?? '?'} ·{' '}
        {parsed.total_events ?? 0} events
      </p>
      {active.length === 0 ? (
        <p className="text-xs text-gray-600">
          No account activity in the window — the correlation surfaced nothing.
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-1 pr-2 font-medium">Account</th>
              <th className="pb-1 pr-2 font-medium">Events</th>
              <th className="pb-1 font-medium">Sessions / locations</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brass/10">
            {active.map((a) => (
              <tr key={a.account_id ?? a.upn ?? 'unknown'} className="align-top">
                <td className="py-1 pr-2 font-medium text-navy">
                  {a.upn ?? a.account_id ?? 'unknown'}
                </td>
                <td className="py-1 pr-2 text-gray-600">{a.events?.length ?? 0}</td>
                <td className="py-1 text-gray-600">
                  {(a.events ?? []).slice(0, 4).map((e, i) => (
                    <div key={i} className="whitespace-nowrap">
                      {e.at ? e.at.replace('T', ' ').slice(0, 19) : '?'} ·{' '}
                      {e.event_type ?? '?'}
                      {e.location?.city ? ` · ${e.location.city}` : ''}
                      {e.session_id ? ` · ${e.session_id}` : ''}
                    </div>
                  ))}
                  {(a.events?.length ?? 0) > 4 && (
                    <div className="text-gray-400">
                      +{(a.events?.length ?? 0) - 4} more
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {quiet.length > 0 && (
        <p className="mt-1.5 text-[11px] text-gray-500">
          Quiet accounts: {quiet.map((q) => q.upn ?? q.account_id).join(', ')}
        </p>
      )}
    </div>
  )
}

// ── Patterns-of-life / baselines result ─────────────────────────────

function BaselinesResult({ call }: { call: LifecycleToolCall }) {
  const parsed = parseToolResult(call.result) as {
    principal_id?: string
    baselines?: Array<Record<string, unknown>>
    patterns_of_life?: Record<string, unknown> | null
  } | null

  if (!parsed) return <RawResult call={call} />

  return (
    <div className="mt-1.5 rounded border border-info/30 bg-info/5 p-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-info mb-1.5 flex items-center gap-1">
        <Users className="w-3 h-3" aria-hidden="true" />
        Patterns of life / baselines used
      </p>
      {parsed.patterns_of_life ? (
        <pre className="text-[11px] leading-4 text-gray-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
          {JSON.stringify(parsed.patterns_of_life, null, 1)}
        </pre>
      ) : (
        <p className="text-xs text-gray-600">No patterns-of-life on record for this principal.</p>
      )}
      {parsed.baselines && parsed.baselines.length > 0 && (
        <pre className="mt-1 text-[11px] leading-4 text-gray-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
          {JSON.stringify(parsed.baselines, null, 1)}
        </pre>
      )}
    </div>
  )
}

// ── Generic tool result ─────────────────────────────────────────────

function RawResult({ call }: { call: LifecycleToolCall }) {
  return (
    <pre className="mt-1.5 rounded bg-navy/[0.04] p-2 text-[11px] leading-4 text-gray-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
      {call.result ?? '(no result recorded)'}
    </pre>
  )
}

function ToolCallRow({ call }: { call: LifecycleToolCall }) {
  const [open, setOpen] = useState(
    call.name === 'get_tenant_activity_window', // correlation opens by default
  )
  const argsCompact = JSON.stringify(call.arguments ?? {})

  return (
    <div className="border-l-2 border-gray-200 pl-2.5">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-left w-full py-0.5"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-3 h-3 text-gray-400 shrink-0" aria-hidden="true" />
        )}
        <Wrench className="w-3 h-3 text-brass shrink-0" aria-hidden="true" />
        <code className="text-xs font-semibold text-navy">{call.name}</code>
        <code className="text-[11px] text-gray-500 truncate">{argsCompact}</code>
      </button>
      {open &&
        (call.name === 'get_tenant_activity_window' ? (
          <TenantActivityResult call={call} />
        ) : call.name === 'get_context_baselines' ? (
          <BaselinesResult call={call} />
        ) : (
          <RawResult call={call} />
        ))}
    </div>
  )
}

// ── Tier section ────────────────────────────────────────────────────

function TierSection({
  tierKey,
  tier,
  decided,
}: {
  tierKey: string
  tier: LifecycleTier
  decided: boolean
}) {
  if (tier.failed) {
    return (
      <div className="rounded-lg border border-danger/40 bg-danger-light p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wide text-danger">
            {TIER_LABELS[tierKey] ?? tier.tier}
          </span>
          <AlertTriangle className="w-3.5 h-3.5 text-danger" aria-hidden="true" />
          <span className="text-xs text-danger font-medium">
            {tier.timed_out ? 'Timed out' : 'Failed'} after {tier.attempts ?? '?'} attempts
            — the investigation did NOT complete at this tier
          </span>
        </div>
        {tier.error && (
          <p className="mt-1 text-[11px] text-danger/80 break-words">{tier.error}</p>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        decided ? 'border-brass bg-brass/5' : 'border-gray-200 bg-white',
      )}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold uppercase tracking-wide text-navy">
          {TIER_LABELS[tierKey] ?? tier.tier}
        </span>
        {decided && (
          <span className="px-1.5 py-0.5 rounded bg-brass text-white text-[10px] font-bold uppercase tracking-wide">
            Decided
          </span>
        )}
        {tier.decision && (
          <code className="px-1.5 py-0.5 rounded bg-navy/5 text-[11px] font-semibold text-navy">
            {tier.decision}
          </code>
        )}
        {tier.model && <span className="text-[11px] text-gray-500">{tier.model}</span>}
        <span className="ml-auto text-[11px] text-gray-500 whitespace-nowrap">
          {tier.severity != null && <>sev {tier.severity} · </>}
          {tier.confidence != null && <>conf {Math.round(tier.confidence * 100)}% · </>}
          {tier.latency_ms != null && <>{Math.round(tier.latency_ms)}ms</>}
        </span>
      </div>

      {(tier.narrative || tier.reasoning) && (
        <p className="mt-1.5 text-xs leading-5 text-gray-700 whitespace-pre-wrap">
          {tier.narrative || tier.reasoning}
        </p>
      )}

      {tier.indicators && tier.indicators.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
          {tier.indicators.map((ind) => (
            <span
              key={ind}
              className="px-1.5 py-0.5 rounded bg-warning-light text-warning text-[10px] font-medium"
            >
              {ind}
            </span>
          ))}
        </div>
      )}

      {tier.tool_calls && tier.tool_calls.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
            Tool calls ({tier.tool_calls.length})
          </p>
          {tier.tool_calls.map((call, i) => (
            <ToolCallRow key={`${call.name}-${i}`} call={call} />
          ))}
        </div>
      )}
      {tier.tool_calls && tier.tool_calls.length === 0 && tierKey !== 'l1' && (
        <p className="mt-1.5 text-[11px] text-gray-400 italic">
          No tool calls recorded for this tier.
        </p>
      )}
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────

export function InvestigationDetail({
  investigation,
}: {
  investigation: LifecycleInvestigation | null
}) {
  if (!investigation) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center">
        <Radar className="w-5 h-5 text-gray-400 mx-auto mb-1" aria-hidden="true" />
        <p className="text-xs text-gray-500">
          No investigation record is persisted for this case. Nothing is shown
          because nothing was recorded.
        </p>
      </div>
    )
  }

  const tierKeys = (['l1', 'l2', 'l3'] as const).filter(
    (k) => investigation[k] != null,
  )
  // A failed final stage must not make an earlier escalation look conclusive.
  const failed = investigation.verdict === 'error' || tierKeys.some(
    (k) => investigation[k]?.failed || investigation[k]?.decision === 'error',
  )
  const latestCoverage = [...tierKeys].reverse().map(
    (k) => investigation[k]?.operational_coverage,
  ).find((coverage) => coverage != null)
  const incomplete = failed || investigation.analysis_status === 'incomplete'
    || latestCoverage?.status === 'incomplete'
  const decidedKey = incomplete ? undefined : [...tierKeys].reverse().find(
    (k) => !investigation[k]?.failed,
  )

  const hasStructure = tierKeys.length > 0 || investigation.verdict

  if (!hasStructure) {
    // Legacy / unknown record shape — show it raw rather than pretending.
    return (
      <details className="rounded-lg border border-gray-200 p-3">
        <summary className="text-xs font-medium text-gray-600 cursor-pointer">
          Investigation record (unrecognised format — raw)
        </summary>
        <pre className="mt-2 text-[11px] leading-4 text-gray-700 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
          {JSON.stringify(investigation, null, 1)}
        </pre>
      </details>
    )
  }

  return (
    <div className="space-y-2">
      {incomplete && (
        <div role="status" className="rounded-lg border border-warning/40 bg-warning-light p-3">
          <p className="text-xs font-semibold">Investigation incomplete</p>
          <p className="mt-1 text-xs">
            {failed
              ? 'An analysis stage failed. No completed security conclusion is available.'
              : 'Some evidence was not read. The recorded verdict does not establish an all-clear.'}
          </p>
        </div>
      )}
      {!incomplete && (!investigation.analysis_status || investigation.analysis_status === 'unknown') && !latestCoverage && (
        <p className="text-xs text-gray-500">Investigation completeness was not recorded.</p>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Brain className="w-3.5 h-3.5 text-brass" aria-hidden="true" />
        <span className="text-xs font-semibold text-navy">
          Verdict:{' '}
          <code className="px-1.5 py-0.5 rounded bg-navy/5">
            {investigation.verdict ?? 'not recorded'}
          </code>
        </span>
        {investigation.recommended_action && (
          <span className="text-xs text-gray-600">
            Recommended (NOT executed — response disarmed):{' '}
            <code className="text-[11px]">{investigation.recommended_action}</code>
          </span>
        )}
        {investigation.captured_at && (
          <span className="ml-auto text-[11px] text-gray-400">
            captured {investigation.captured_at}
          </span>
        )}
      </div>
      {tierKeys.map((k) => (
        <TierSection
          key={k}
          tierKey={k}
          tier={investigation[k] as LifecycleTier}
          decided={k === decidedKey}
        />
      ))}
    </div>
  )
}
